import React, { useState, useEffect, useCallback } from 'react';
import {
  Pane, Card, Heading, Text, Table, Badge, Button, toaster, Spinner, IconButton
} from 'evergreen-ui';
import { useProject } from '../../context/ProjectContext';
import api from '../../services/api';
import CreateInviteDialog from './CreateInviteDialog';

const roleBadgeColor = {
  owner: 'red', admin: 'orange', member: 'blue', viewer: 'neutral'
};

const ProjectMembers = () => {
  const { currentProject } = useProject();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('member');
  const [showCreateInvite, setShowCreateInvite] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentProject?.id) return;
    setLoading(true);
    try {
      const membersRes = await api.get(`/projects/${currentProject.id}/members`);
      setMembers(membersRes.data);

      // Find current user's role
      const me = membersRes.data.find(m =>
        // The current user's email - we don't have it directly, so check by role logic
        true
      );

      // Try to fetch invites (will fail with 403 if not admin/owner)
      try {
        const invitesRes = await api.get(`/projects/${currentProject.id}/invites`);
        setInvites(invitesRes.data);
        // If we can see invites, we're at least admin
        setUserRole('admin');
      } catch {
        setUserRole('member');
      }
    } catch (err) {
      if (err.response?.status !== 403) {
        toaster.danger('Failed to load members');
      }
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRemoveMember = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from this project?`)) return;
    try {
      await api.delete(`/projects/${currentProject.id}/members/${userId}`);
      toaster.success(`${email} removed`);
      fetchData();
    } catch (err) {
      toaster.danger(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await api.delete(`/projects/${currentProject.id}/invites/${inviteId}`);
      toaster.success('Invite revoked');
      fetchData();
    } catch (err) {
      toaster.danger(err.response?.data?.detail || 'Failed to revoke invite');
    }
  };

  if (!currentProject) {
    return (
      <Card background="tint1" padding={16}>
        <Heading size={500} marginBottom={16}>Project Members</Heading>
        <Text color="muted">Please select a project first.</Text>
      </Card>
    );
  }

  if (loading) {
    return (
      <Pane display="flex" justifyContent="center" padding={64}>
        <Spinner />
      </Pane>
    );
  }

  const isManager = userRole === 'admin' || userRole === 'owner';

  return (
    <Pane padding={16} maxWidth={900} marginX="auto">
      {/* Members */}
      <Card background="tint1" padding={16} marginBottom={16}>
        <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
          <Heading size={500}>Members ({members.length})</Heading>
          {isManager && (
            <Button appearance="primary" onClick={() => setShowCreateInvite(true)}>
              Create Invite Link
            </Button>
          )}
        </Pane>

        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Email</Table.TextHeaderCell>
            <Table.TextHeaderCell>Role</Table.TextHeaderCell>
            <Table.TextHeaderCell>Permissions</Table.TextHeaderCell>
            <Table.TextHeaderCell>Joined</Table.TextHeaderCell>
            {isManager && <Table.TextHeaderCell>Actions</Table.TextHeaderCell>}
          </Table.Head>
          <Table.Body>
            {members.map(m => (
              <Table.Row key={m.user_id}>
                <Table.TextCell>{m.email}</Table.TextCell>
                <Table.TextCell>
                  <Badge color={roleBadgeColor[m.role] || 'neutral'}>
                    {m.role.toUpperCase()}
                  </Badge>
                </Table.TextCell>
                <Table.TextCell>
                  {m.can_write ? 'Read/Write' : 'Read Only'}
                </Table.TextCell>
                <Table.TextCell>
                  {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '-'}
                </Table.TextCell>
                {isManager && (
                  <Table.TextCell>
                    {m.role !== 'owner' && (
                      <Button
                        intent="danger"
                        size="small"
                        onClick={() => handleRemoveMember(m.user_id, m.email)}
                      >
                        Remove
                      </Button>
                    )}
                  </Table.TextCell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card>

      {/* Invites */}
      {isManager && invites.length > 0 && (
        <Card background="tint1" padding={16}>
          <Heading size={500} marginBottom={16}>Invite Links ({invites.length})</Heading>
          <Table>
            <Table.Head>
              <Table.TextHeaderCell>Token</Table.TextHeaderCell>
              <Table.TextHeaderCell>Role</Table.TextHeaderCell>
              <Table.TextHeaderCell>Uses</Table.TextHeaderCell>
              <Table.TextHeaderCell>Status</Table.TextHeaderCell>
              <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
            </Table.Head>
            <Table.Body>
              {invites.map(inv => {
                const isExpired = inv.expires_at && new Date(inv.expires_at) < new Date();
                const isMaxed = inv.max_uses !== null && inv.uses_count >= inv.max_uses;
                const isActive = inv.is_active && !isExpired && !isMaxed;

                return (
                  <Table.Row key={inv.id}>
                    <Table.TextCell>
                      <Text fontFamily="mono" fontSize={12}>
                        ...{inv.token.slice(-8)}
                      </Text>
                    </Table.TextCell>
                    <Table.TextCell>
                      <Badge color={roleBadgeColor[inv.role] || 'neutral'}>
                        {inv.role.toUpperCase()}
                      </Badge>
                    </Table.TextCell>
                    <Table.TextCell>
                      {inv.uses_count}{inv.max_uses !== null ? `/${inv.max_uses}` : ''}
                    </Table.TextCell>
                    <Table.TextCell>
                      <Badge color={isActive ? 'green' : 'red'}>
                        {isActive ? 'Active' : isExpired ? 'Expired' : isMaxed ? 'Maxed' : 'Revoked'}
                      </Badge>
                    </Table.TextCell>
                    <Table.TextCell>
                      {inv.is_active && (
                        <Button
                          intent="danger"
                          size="small"
                          onClick={() => handleRevokeInvite(inv.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </Table.TextCell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </Card>
      )}

      <CreateInviteDialog
        isShown={showCreateInvite}
        onClose={() => { setShowCreateInvite(false); fetchData(); }}
        projectId={currentProject.id}
        userRole={userRole}
      />
    </Pane>
  );
};

export default ProjectMembers;
